/* eslint-disable react/require-default-props, react/forbid-prop-types, import/extensions */
import React, { useState, useMemo, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronRight,
  ChevronDown,
  BookOpen,
  Video,
  FileText,
  Link as LinkIcon,
  ExternalLink,
  Clock,
  CheckCircle2,
  Circle,
  MoreHorizontal,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import GoalsConstants from '../../../../constants/goals.constants';
import UserContext from '../../../../hooks/context/UserContext';
import { cn } from '../../../../lib/utils';
import ReportIssueModal from './ReportIssueModal';

const statusConfig = {
  IN_PROGRESS: { icon: Clock, color: 'warning', label: 'In Progress' },
  DONE: { icon: CheckCircle2, color: 'success', label: 'Done' },
  APPROVED: { icon: CheckCircle2, color: 'success', label: 'Approved' },
  DEFAULT: { icon: Circle, color: 'secondary', label: 'Not Started' },
};

const materialIcons = {
  video: Video,
  article: FileText,
  book: BookOpen,
  link: LinkIcon,
};

function DetailItem({ detail, skill, onLinkClick, onStatusChange, onReportIssue, isDisabled }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const MaterialIcon = materialIcons[detail.materialType?.toLowerCase()] || LinkIcon;
  const detailStatusInfo = statusConfig[detail.status] || statusConfig.DEFAULT;
  const DetailStatusIcon = detailStatusInfo.icon;

  const isInProgress = detail.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS;
  const isDone = detail.status === GoalsConstants.GOAL_STATUSES.DONE;
  const isApproved = detail.status === GoalsConstants.GOAL_STATUSES.APPROVED;

  const canShowMenu = !isDisabled && !isApproved && (isInProgress || isDone);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  const handleDone = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onStatusChange({
      parentId: skill.itemId,
      itemId: detail.itemId,
      status: GoalsConstants.GOAL_STATUSES.DONE,
    });
  };

  const handleInProgress = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onStatusChange({
      parentId: skill.itemId,
      itemId: detail.itemId,
      status: GoalsConstants.GOAL_STATUSES.IN_PROGRESS,
    });
  };

  const handleReportIssue = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onReportIssue({ skill, detail });
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-background rounded-md group">
      <DetailStatusIcon
        className={cn('h-4 w-4 mt-0.5 shrink-0', {
          'text-amber-500': detailStatusInfo.color === 'warning',
          'text-green-500': detailStatusInfo.color === 'success',
          'text-muted-foreground': detailStatusInfo.color === 'secondary',
        })}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-base font-medium">{detail.name}</p>
        {detail.successCriteria && (
          <p className="text-xs text-muted-foreground">{detail.successCriteria}</p>
        )}
        {detail.link && (
          <a
            href={detail.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
            onClick={(e) => {
              e.stopPropagation();
              onLinkClick?.({ taskId: detail.itemId, link: detail.link });
            }}
          >
            <MaterialIcon className="h-3 w-3" />
            {detail.materialName || 'View Material'}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {canShowMenu && (
        <div className="relative" ref={menuRef}>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleMenuClick}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>

          {menuOpen && (
            <div className="absolute right-0 top-8 z-50 min-w-[140px] rounded-md border bg-popover p-1 shadow-md">
              {isInProgress && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                  onClick={handleDone}
                >
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  Done
                </button>
              )}
              {isDone && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                  onClick={handleInProgress}
                >
                  <RotateCcw className="h-4 w-4 text-amber-500" />
                  In Progress
                </button>
              )}
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent text-destructive"
                onClick={handleReportIssue}
              >
                <AlertTriangle className="h-4 w-4" />
                Report Issue
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

DetailItem.propTypes = {
  detail: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string,
    successCriteria: PropTypes.string,
    materialName: PropTypes.string,
    materialType: PropTypes.string,
    link: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  skill: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  onLinkClick: PropTypes.func,
  onStatusChange: PropTypes.func.isRequired,
  onReportIssue: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

function SkillItem({
  skill,
  isExpanded,
  onToggle,
  onLinkClick,
  onStatusChange,
  onReportIssue,
  isDisabled,
}) {
  const statusInfo = statusConfig[skill.status] || statusConfig.DEFAULT;
  const StatusIcon = statusInfo.icon;

  const completedDetails =
    skill.details?.filter((d) => d.status === 'DONE' || d.status === 'APPROVED').length || 0;
  const totalDetails = skill.details?.length || 0;

  return (
    <div className="border rounded-lg overflow-hidden transition-all hover:shadow-sm">
      <button
        type="button"
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="shrink-0">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <StatusIcon
          className={cn('h-5 w-5 shrink-0', {
            'text-amber-500': statusInfo.color === 'warning',
            'text-green-500': statusInfo.color === 'success',
            'text-muted-foreground': statusInfo.color === 'secondary',
          })}
        />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-base truncate">{skill.name}</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {completedDetails} of {totalDetails} tasks completed
          </p>
        </div>
        <Badge variant={statusInfo.color} className="shrink-0">
          {statusInfo.label}
        </Badge>
      </button>

      {isExpanded && skill.details && skill.details.length > 0 && (
        <div className="border-t bg-muted/30 p-4 space-y-3">
          {skill.details.map((detail) => (
            <DetailItem
              key={detail.itemId || detail.name}
              detail={detail}
              skill={skill}
              onLinkClick={onLinkClick}
              onStatusChange={onStatusChange}
              onReportIssue={onReportIssue}
              isDisabled={isDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}

SkillItem.propTypes = {
  skill: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string.isRequired,
    status: PropTypes.string,
    details: PropTypes.arrayOf(
      PropTypes.shape({
        itemId: PropTypes.string,
        name: PropTypes.string,
        successCriteria: PropTypes.string,
        materialName: PropTypes.string,
        materialType: PropTypes.string,
        link: PropTypes.string,
        status: PropTypes.string,
      }),
    ),
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func,
  onStatusChange: PropTypes.func.isRequired,
  onReportIssue: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

function SkillsPanel({ goalsInProgress, goalsDone, onLinkClick, isDisabled }) {
  const [expandedSkills, setExpandedSkills] = useState(new Set());
  const [activeTab, setActiveTab] = useState('in-progress');
  const [reportModalData, setReportModalData] = useState(null);

  const { socketInstance } = useContext(UserContext);

  const toggleSkill = (skillId) => {
    setExpandedSkills((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(skillId)) {
        newSet.delete(skillId);
      } else {
        newSet.add(skillId);
      }
      return newSet;
    });
  };

  const handleStatusChange = ({ parentId, itemId, status }) => {
    if (socketInstance) {
      socketInstance.emit('send-nested-goal-status', {
        parentId,
        itemId,
        status,
      });
    }
  };

  const handleReportIssue = ({ skill, detail }) => {
    setReportModalData({ skill, detail });
  };

  const handleCloseReportModal = () => {
    setReportModalData(null);
  };

  const inProgressDetails = useMemo(() => {
    let count = 0;
    goalsInProgress.forEach((skill) => {
      skill.details?.forEach((detail) => {
        if (detail.status !== 'DONE' && detail.status !== 'APPROVED') {
          count += 1;
        }
      });
    });
    return count;
  }, [goalsInProgress]);

  const doneDetails = useMemo(() => {
    let count = 0;
    goalsDone.forEach((skill) => {
      skill.details?.forEach(() => {
        count += 1;
      });
    });
    return count;
  }, [goalsDone]);

  const currentSkills = activeTab === 'in-progress' ? goalsInProgress : goalsDone;

  return (
    <>
      <Card className="h-full flex flex-col overflow-hidden">
        <CardHeader className="pb-3 shrink-0">
          <CardTitle>My Skills</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            <TabsList className="grid w-full grid-cols-2 shrink-0">
              <TabsTrigger value="in-progress" className="gap-2">
                In Progress
                <Badge variant="warning" className="ml-1 h-5 px-1.5">
                  {inProgressDetails}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="done" className="gap-2">
                Completed
                <Badge variant="success" className="ml-1 h-5 px-1.5">
                  {doneDetails}
                </Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-4 flex-1 min-h-0 overflow-hidden">
              <ScrollArea className="h-full pr-4">
                {currentSkills.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="rounded-full bg-muted p-4 mb-4">
                      {activeTab === 'in-progress' ? (
                        <Clock className="h-8 w-8 text-muted-foreground" />
                      ) : (
                        <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                    <p className="text-muted-foreground">
                      {activeTab === 'in-progress'
                        ? 'No skills in progress. Add some from the skill tree!'
                        : 'No completed skills yet. Keep learning!'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {currentSkills.map((skill) => (
                      <SkillItem
                        key={skill.itemId}
                        skill={skill}
                        isExpanded={expandedSkills.has(skill.itemId)}
                        onToggle={() => toggleSkill(skill.itemId)}
                        onLinkClick={onLinkClick}
                        onStatusChange={handleStatusChange}
                        onReportIssue={handleReportIssue}
                        isDisabled={isDisabled}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {reportModalData && (
        <ReportIssueModal
          isOpen={!!reportModalData}
          onClose={handleCloseReportModal}
          skill={reportModalData.skill}
          detail={reportModalData.detail}
        />
      )}
    </>
  );
}

SkillsPanel.propTypes = {
  goalsInProgress: PropTypes.array,
  goalsDone: PropTypes.array,
  onLinkClick: PropTypes.func,
  isDisabled: PropTypes.bool,
};

SkillsPanel.defaultProps = {
  goalsInProgress: [],
  goalsDone: [],
  onLinkClick: () => {},
  isDisabled: false,
};

export default SkillsPanel;
